import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { toastHandler } from '@/components/utils/Toast'
import { useAuthContext } from '@/context'
import { keyDocument, keyDocuments } from '@/services/folders/queries'
import { useUploadMediaAssets } from '@/services/media/mutations'
import { keyMediaAssets } from '@/services/media/queries'
import { AlertCircle, CheckCircle2, File, FileImage, FileText, Loader2, Upload, X, XCircle } from 'lucide-react'
import { FormEvent, ReactNode, useCallback, useEffect, useState } from 'react'
import { FileRejection, useDropzone } from 'react-dropzone'
import { useParams } from 'react-router'

type Props = {
    trigger: ReactNode
    open?: boolean
    onOpenChange?: (open: boolean) => void
    initialFiles?: File[]
    inDropdown?: boolean
    folderId?: string
    onSelectMedia?: (media: Array<{ url: string; name: string; type: string }>) => void
}

type ImageUploadStatus = {
    id: number
    name: string
    uploading: boolean
    uploadSuccess: boolean
    uploadFailed?: boolean
    uploadProgress?: number
    file: File
    size: number
    type: string
}

const ManageUploadMediaFiles = ({ trigger, open, onOpenChange, initialFiles, inDropdown, folderId, onSelectMedia }: Props) => {
    const [isOpen, setIsOpen] = useState(false)
    const params = useParams()
    const { orgAccount, isLoading: isAuthLoading } = useAuthContext()
    const [uploadedImages, setUploadedImages] = useState<ImageUploadStatus[]>([])
    const [previews, setPreviews] = useState<Record<number, string>>({})
    const [uploadCompleted, setUploadCompleted] = useState(false)
    const [progressInterval, setProgressInterval] = useState<NodeJS.Timeout | null>(null)

    const { mutate: uploadAssets, isLoading } = useUploadMediaAssets({
        refetch: [keyMediaAssets(''), keyDocument(`id=${params.folder}`), keyDocuments(``)],
        onSuccess: (data) => {
            console.log('📸 Upload onSuccess - Raw data:', data)
            console.log('📸 Data type:', typeof data, 'Is array:', Array.isArray(data))
            console.log('📸 Data keys:', data ? Object.keys(data) : 'null')
            
            // Clear progress interval
            if (progressInterval) {
                clearInterval(progressInterval)
                setProgressInterval(null)
            }

            toastHandler({ message: 'Upload successful' })
            setUploadCompleted(true)
            // Mark all files as successfully uploaded
            setUploadedImages(prevImages =>
                prevImages.map(img => ({
                    ...img,
                    uploading: false,
                    uploadSuccess: true,
                    uploadFailed: false,
                    uploadProgress: 100,
                }))
            )
            
            // Call the callback if provided with uploaded media URLs
            if (onSelectMedia) {
                console.log('📸 onSelectMedia callback exists, processing data...')
                
                // Handle different response structures
                let mediaData: Array<{ url: string; name: string; type: string }> = []
                
                if (data && typeof data === 'object') {
                    // Check various possible data structures
                    const possibleDataPaths = [
                        (data as any).details, // ← Our upload API returns { details: [...] }
                        (data as any).data,
                        (data as any).files,
                        (data as any).file,
                        (data as any).media,
                        (data as any).assets,
                        data,
                    ]
                    
                    for (const possibleData of possibleDataPaths) {
                        if (!possibleData) continue
                        
                        console.log('📸 Checking data path:', possibleData)
                        console.log('📸 Is array?', Array.isArray(possibleData))
                        
                        if (Array.isArray(possibleData) && possibleData.length > 0) {
                            console.log('📸 Found array with', possibleData.length, 'items')
                            mediaData = possibleData.map((item: any) => {
                                console.log('📸 Processing array item:', item)
                                return {
                                    url: item.url || item.Location || item.location || item.cdnUrl || item.fileUrl || item.Key || '',
                                    name: item.name || item.file || item.key || item.originalName || item.fileName || item.Key || '',
                                    type: item.type || item.mimeType || item.contentType || item.ContentType || '',
                                }
                            })
                            break
                        } else if (possibleData && (possibleData.url || possibleData.Location || possibleData.location || possibleData.cdnUrl)) {
                            // Single file object
                            console.log('📸 Found single file object:', possibleData)
                            mediaData = [{
                                url: possibleData.url || possibleData.Location || possibleData.location || possibleData.cdnUrl || possibleData.fileUrl || possibleData.Key || '',
                                name: possibleData.name || possibleData.file || possibleData.key || possibleData.originalName || possibleData.fileName || possibleData.Key || '',
                                type: possibleData.type || possibleData.mimeType || possibleData.contentType || possibleData.ContentType || '',
                            }]
                            break
                        }
                    }
                }
                
                console.log('📸 Final mediaData:', mediaData)
                if (mediaData.length > 0) {
                    console.log('📸 Calling onSelectMedia with:', mediaData)
                    onSelectMedia(mediaData)
                } else {
                    console.warn('📸 No media data to send to callback. Full response:', JSON.stringify(data, null, 2))
                }
            } else {
                console.log('📸 No onSelectMedia callback provided')
            }
        },
        onError: (error: any) => {
            // Clear progress interval
            if (progressInterval) {
                clearInterval(progressInterval)
                setProgressInterval(null)
            }

            if (error) {
                toastHandler({ message: `Upload failed: ${error.message || 'Unknown error'}`, state: 'error' })
            }
            setUploadCompleted(true)
            // Mark all files as failed
            setUploadedImages(prevImages =>
                prevImages.map(img => ({
                    ...img,
                    uploading: false,
                    uploadFailed: true,
                    uploadSuccess: false,
                    uploadProgress: 0,
                }))
            )
        },
    })

    const formatFileSize = (bytes: number) => {
        const sizes = ['B', 'KB', 'MB', 'GB']
        if (bytes === 0) return '0 B'
        const i = Math.floor(Math.log(bytes) / Math.log(1024))
        return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i]
    }

    const removeFile = useCallback(
        (fileId: number) => {
            // Clean up preview URL for this specific file
            if (previews[fileId]) {
                URL.revokeObjectURL(previews[fileId])
                setPreviews(prev => {
                    const newPreviews = { ...prev }
                    delete newPreviews[fileId]
                    return newPreviews
                })
            }

            setUploadedImages(prevImages => {
                return prevImages.filter(img => img.id !== fileId)
            })
        },
        [previews]
    )

    const handleFiles = useCallback((acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
        if (rejectedFiles.length > 0) {
            const errorMessages = rejectedFiles.map(rejection => {
                const errors = rejection.errors
                    .map(error => {
                        switch (error.code) {
                            case 'file-too-large':
                                return 'File is too large (max 50MB)'
                            case 'file-invalid-type':
                                return 'Invalid file type. Supported types: JPG, PNG, SVG, PDF, TXT'
                            case 'too-many-files':
                                return 'Too many files selected (max 50 per upload)'
                            default:
                                return error.message
                        }
                    })
                    .join(', ')
                return `${rejection.file.name}: ${errors}`
            })

            toastHandler({
                message: `Some files were rejected:\n${errorMessages.join('\n')}`,
                state: 'error',
            })
        }

        const baseId = Date.now()
        const newImages = acceptedFiles.map((file, index) => ({
            id: baseId + index,
            name: file.name,
            uploading: false,
            uploadSuccess: false,
            uploadFailed: false,
            uploadProgress: 0,
            file: file, // Use the file directly without creating a new File object
            size: file.size,
            type: file.type,
        }))

        setUploadedImages(prevImages => [...prevImages, ...newImages])

        // generate previews for images
        newImages.forEach(img => {
            if (img.file.type.startsWith('image/')) {
                try {
                    const url = URL.createObjectURL(img.file)
                    setPreviews(prev => ({ ...prev, [img.id]: url }))
                } catch (error) {
                    console.warn('Failed to create blob URL for file:', img.file.name, error)
                }
            }
        })
    }, [])

    const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
        onDrop: handleFiles,
        accept: {
            'image/jpeg': ['.jpg', '.jpeg'],
            'image/png': ['.png'],
            'image/svg+xml': ['.svg'],
            'application/pdf': ['.pdf'],
            'text/plain': ['.txt'],
        },
        multiple: true,
        maxSize: 50 * 1024 * 1024,
        maxFiles: 50,
    })

    const resetUploadState = useCallback(() => {
        // Clear any running progress interval
        setProgressInterval(currentInterval => {
            if (currentInterval) {
                clearInterval(currentInterval)
            }
            return null
        })

        setUploadedImages([])
        setUploadCompleted(false)
    }, [])

    // Separate function to clean up blob URLs only when really needed
    const cleanupBlobUrls = useCallback(() => {
        setPreviews(currentPreviews => {
            Object.values(currentPreviews).forEach(url => {
                if (url.startsWith('blob:')) {
                    URL.revokeObjectURL(url)
                }
            })
            return {}
        })
    }, [])

    useEffect(() => {
        if (!open) {
            // Only reset state, don't clean up blob URLs immediately
            // This allows users to see previews while modal is open
            resetUploadState()
        }
    }, [open, resetUploadState])

    // Clean up blob URLs when component unmounts
    useEffect(() => {
        return () => {
            cleanupBlobUrls()
        }
    }, [cleanupBlobUrls])

    useEffect(() => {
        if (initialFiles && initialFiles.length > 0) {
            const baseId = Date.now()
            const newImages = initialFiles.map((file, index) => ({
                id: baseId + index,
                name: file.name,
                uploading: false,
                uploadSuccess: false,
                uploadFailed: false,
                uploadProgress: 0,
                file,
                size: file.size,
                type: file.type,
            }))
            setUploadedImages(newImages)
            newImages.forEach(img => {
                if (img.file.type.startsWith('image/')) {
                    try {
                        const url = URL.createObjectURL(img.file)
                        setPreviews(prev => ({ ...prev, [img.id]: url }))
                    } catch (error) {
                        console.warn('Failed to create blob URL for initial file:', img.file.name, error)
                    }
                }
            })
        }
    }, [initialFiles])

    const onSubmit = (e: FormEvent) => {
        e.preventDefault()

        console.log('🚀 Upload form submitted')
        console.log('📁 Upload files count:', uploadedImages.length)
        console.log('👤 orgAccount:', orgAccount)
        console.log('🔐 isAuthLoading:', isAuthLoading)
        console.log('📂 params:', params)
        console.log('🔧 isLoading:', isLoading)

        if (uploadedImages.length < 1) {
            console.log('❌ No files to upload')
            return
        }

        if (isAuthLoading) {
            console.log('⏳ Auth still loading, please wait...')
            toastHandler({ message: 'Loading workspace data, please wait...', state: 'warning' })
            return
        }

        if (!orgAccount?.org?.id) {
            console.log('❌ Missing orgAccount.org.id:', orgAccount?.org?.id)
            toastHandler({ message: 'Workspace not loaded. Please refresh the page.', state: 'error' })
            return
        }

        // Note: Media uploads don't require project_id - they work at org level
        console.log('📁 Upload context:', {
            folderId: folderId || 'root',
            orgId: orgAccount?.org?.id,
            hasFiles: uploadedImages.length > 0,
        })

        console.log('✅ Starting upload process...')

        // Mark all files as uploading
        setUploadedImages(prevImages =>
            prevImages.map(img => ({
                ...img,
                uploading: true,
                uploadProgress: 0,
                uploadSuccess: false,
                uploadFailed: false,
            }))
        )

        // Simulate progress for better UX
        const interval = setInterval(() => {
            setUploadedImages(prevImages =>
                prevImages.map(img => {
                    if (img.uploading && img?.uploadProgress && img.uploadProgress < 90) {
                        return { ...img, uploadProgress: Math.min(90, img.uploadProgress + Math.random() * 15 + 5) }
                    }
                    return img
                })
            )
        }, 150)

        setProgressInterval(interval)

        const formData = new FormData()

        // Use 'files' as the field name for all files (multer expects this)
        for (let i = 0; i < uploadedImages.length; i++) {
            formData.append('files', uploadedImages[i].file)
        }

        // Backend expects: org, data_type, and optional parent (folder ID)
        formData.set('org', orgAccount?.org?.id || '')
        formData.set('data_type', 'image')

        if (folderId) {
            formData.set('parent', folderId)
        }

        console.log('📤 Calling uploadAssets with formData')
        uploadAssets(formData as any)
    }

    const loading = isLoading

    // Start progress simulation when mutation loading begins
    useEffect(() => {
        if (loading && !uploadCompleted && uploadedImages.length > 0) {
            // Ensure all files show as uploading when mutation starts
            setUploadedImages(prevImages =>
                prevImages.map(img => ({
                    ...img,
                    uploading: true,
                    uploadProgress: Math.max(img.uploadProgress || 0, 10), // Start with at least 10%
                }))
            )
        }
    }, [loading, uploadCompleted, uploadedImages.length])

    const getDropzoneClassName = () => {
        let className = 'transition-all duration-200 border-2 border-dashed rounded-xl '

        if (isDragActive) {
            if (isDragAccept) {
                className += 'border-green-400 bg-green-50'
            } else if (isDragReject) {
                className += 'border-red-400 bg-red-50'
            } else {
                className += 'border-blue-400 bg-blue-50'
            }
        } else {
            className += 'border-gray-300 hover:border-gray-400 bg-gray-50'
        }

        return className
    }

    const FilePreview = ({ image }: { image: ImageUploadStatus }) => {
        const preview = previews[image.id]
        const isPdf = image.type.includes('pdf')
        const isTxt = image.type.includes('text')
        const isImage = image.type.startsWith('image/')

        return (
            <div className='relative p-4 transition-all duration-200 bg-white border border-gray-200 rounded-lg group hover:shadow-md'>
                <button
                    type='button'
                    onClick={() => removeFile(image.id)}
                    className='absolute flex items-center justify-center w-6 h-6 text-white transition-opacity duration-200 bg-red-500 rounded-full opacity-0 -top-2 -right-2 group-hover:opacity-100 hover:bg-red-600'
                >
                    <X className='w-3 h-3' />
                </button>

                <div className='flex items-center gap-3'>
                    <div className='flex items-center justify-center flex-shrink-0 w-12 h-12 overflow-hidden bg-gray-100 rounded-lg'>
                        {preview && isImage ? (
                            <img src={preview} className='object-cover w-full h-full' alt={image.name} />
                        ) : isPdf ? (
                            <FileText className='w-6 h-6 text-red-500' />
                        ) : isTxt ? (
                            <File className='w-6 h-6 text-blue-500' />
                        ) : (
                            <FileImage className='w-6 h-6 text-gray-400' />
                        )}
                    </div>

                    <div className='flex-1 min-w-0'>
                        <h4 className='text-sm font-medium text-gray-900 truncate' title={image.name}>
                            {image.name}
                        </h4>
                        <p className='text-xs text-gray-500'>
                            {formatFileSize(image.size)} • {image.type.split('/')[1]?.toUpperCase() || 'FILE'}
                        </p>

                        {(loading || image.uploading) && !uploadCompleted && (
                            <div className='mt-2'>
                                <div className='flex items-center gap-2 mb-1 text-xs text-gray-500'>
                                    <span>Uploading...</span>
                                    <span>{Math.round(image.uploadProgress || 0)}%</span>
                                </div>
                                <div className='w-full bg-gray-200 rounded-full h-1.5'>
                                    <div
                                        className='bg-blue-500 h-1.5 rounded-full transition-all duration-300'
                                        style={{ width: `${image.uploadProgress || 0}%` }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    <div className='flex items-center justify-center w-6'>
                        {(loading || image.uploading) && !uploadCompleted ? (
                            <Loader2 className='w-5 h-5 text-blue-500 animate-spin' />
                        ) : uploadCompleted ? (
                            image.uploadSuccess ? (
                                <CheckCircle2 className='w-5 h-5 text-green-500' />
                            ) : image.uploadFailed ? (
                                <XCircle className='w-5 h-5 text-red-500' />
                            ) : null
                        ) : null}
                    </div>
                </div>
            </div>
        )
    }

    return (
        <Dialog
            open={open !== undefined ? open : isOpen}
            onOpenChange={value => {
                onOpenChange && onOpenChange(value)
                if (!onOpenChange) {
                    setIsOpen(value)
                }

                if (!value) {
                    resetUploadState()
                }
            }}
        >
            <DialogTrigger asChild className={inDropdown ? 'w-full' : 'w-max'}>
                {trigger}
            </DialogTrigger>
            <DialogContent className='max-w-4xl max-h-[90vh] flex flex-col'>
                <DialogHeader className='flex-shrink-0'>
                    <DialogTitle className='flex items-center gap-2 text-xl'>
                        <Upload className='w-5 h-5' />
                        Upload Media Files
                    </DialogTitle>
                    <p className='mt-1 text-sm text-gray-500'>
                        Upload images, PDFs, and text files to your media library
                    </p>
                </DialogHeader>

                <form onSubmit={onSubmit} className='flex flex-col flex-1 min-h-0'>
                    <div className='flex-1 min-h-0'>
                        {uploadedImages.length > 0 ? (
                            <div className='space-y-4'>
                                <div className='flex items-center justify-between'>
                                    <h3 className='text-lg font-medium'>
                                        Files Ready for Upload ({uploadedImages.length})
                                    </h3>
                                    <Button
                                        type='button'
                                        variant='outline'
                                        size='sm'
                                        onClick={() => {
                                            cleanupBlobUrls()
                                            setUploadedImages([])
                                        }}
                                        disabled={loading}
                                    >
                                        Clear All
                                    </Button>
                                </div>

                                <div className='pr-2 space-y-3 overflow-y-auto max-h-80'>
                                    {uploadedImages.map(image => (
                                        <FilePreview key={image.id} image={image} />
                                    ))}
                                </div>

                                <div {...getRootProps()} className={`${getDropzoneClassName()} p-4 cursor-pointer`}>
                                    <input {...getInputProps()} />
                                    <div className='flex items-center justify-center gap-2 text-sm text-gray-600'>
                                        <Upload className='w-4 h-4' />
                                        {isDragActive ? (
                                            <span>Drop files here to add more</span>
                                        ) : (
                                            <span>Click or drag to add more files</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div
                                {...getRootProps()}
                                className={`${getDropzoneClassName()} min-h-96 flex flex-col items-center justify-center cursor-pointer p-8`}
                            >
                                <input {...getInputProps()} />

                                <div className='flex flex-col items-center gap-4'>
                                    <div className='relative'>
                                        <div className='flex items-center justify-center w-16 h-16 bg-white rounded-full shadow-lg'>
                                            {isDragActive ? (
                                                isDragAccept ? (
                                                    <CheckCircle2 className='w-8 h-8 text-green-500' />
                                                ) : isDragReject ? (
                                                    <AlertCircle className='w-8 h-8 text-red-500' />
                                                ) : (
                                                    <Upload className='w-8 h-8 text-blue-500' />
                                                )
                                            ) : (
                                                <FileImage className='w-8 h-8 text-gray-400' />
                                            )}
                                        </div>
                                        {!isDragActive && (
                                            <div className='absolute flex items-center justify-center w-6 h-6 bg-blue-500 rounded-full -bottom-1 -right-1'>
                                                <Upload className='w-3 h-3 text-white' />
                                            </div>
                                        )}
                                    </div>

                                    <div className='text-center'>
                                        <h3 className='mb-2 text-lg font-medium text-gray-900'>
                                            {isDragActive
                                                ? isDragAccept
                                                    ? 'Drop files to upload'
                                                    : isDragReject
                                                      ? 'Some files are not supported'
                                                      : 'Drop files here'
                                                : 'Drag & drop files here'}
                                        </h3>
                                        <p className='mb-4 text-gray-500'>or click to browse from your computer</p>

                                        <div className='flex items-center justify-center gap-4 text-xs text-gray-400'>
                                            <span>JPEG, PNG, SVG, PDF, TXT</span>
                                            <span>•</span>
                                            <span>Max 10MB each</span>
                                            <span>•</span>
                                            <span>Up to 20 files</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <DialogFooter className='flex-shrink-0 mt-6'>
                        {uploadedImages.length > 0 ? (
                            <div className='flex items-center justify-between w-full'>
                                <div className='text-sm text-gray-500'>
                                    {uploadedImages.length} file{uploadedImages.length !== 1 ? 's' : ''} selected
                                    {uploadedImages.length > 0 && (
                                        <span className='ml-2'>
                                            (
                                            {formatFileSize(uploadedImages.reduce((total, img) => total + img.size, 0))}{' '}
                                            total)
                                        </span>
                                    )}
                                </div>

                                <div className='flex gap-3'>
                                    <Button
                                        type='button'
                                        variant='outline'
                                        onClick={() => {
                                            cleanupBlobUrls()
                                            setIsOpen(false)
                                            if (onOpenChange) onOpenChange(false)
                                            resetUploadState()
                                        }}
                                        disabled={loading}
                                    >
                                        Cancel
                                    </Button>
                                    {uploadCompleted ? (
                                        <Button
                                            type='button'
                                            onClick={() => {
                                                cleanupBlobUrls()
                                                setIsOpen(false)
                                                if (onOpenChange) onOpenChange(false)
                                                resetUploadState()
                                            }}
                                        >
                                            Done
                                        </Button>
                                    ) : (
                                        <Button
                                            type='submit'
                                            disabled={loading || uploadedImages.length === 0 || isAuthLoading || !orgAccount?.org?.id}
                                            className='min-w-[100px]'
                                        >
                                            {loading ? (
                                                <>
                                                    <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                                                    Uploading...
                                                </>
                                            ) : isAuthLoading ? (
                                                <>
                                                    <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                                                    Loading...
                                                </>
                                            ) : !orgAccount?.org?.id ? (
                                                <>
                                                    <AlertCircle className='w-4 h-4 mr-2' />
                                                    Workspace Required
                                                </>
                                            ) : (
                                                <>
                                                    <Upload className='w-4 h-4 mr-2' />
                                                    Upload Files
                                                </>
                                            )}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ) : null}
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    )
}

export default ManageUploadMediaFiles
